import React, { useState, useMemo } from 'react';
import { Zap } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField, SelectField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { ACTIVITIES_MET, calorieBurn } from '../../lib/calculatorUtils';

const CalorieBurnCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [weightKg, setWeightKg] = useState('');
  const [activity, setActivity] = useState('running');
  const [minutes, setMinutes] = useState('');

  const met = ACTIVITIES_MET.find((a) => a.value === activity)?.met || 5;

  const result = useMemo(() => {
    const w = parseFloat(weightKg);
    const m = parseFloat(minutes);
    if (!w || !m || w < 20 || m <= 0) return null;
    const kcal = calorieBurn(met, w, m);
    return { kcal, perMin: +(kcal / m).toFixed(1) };
  }, [weightKg, minutes, met]);

  const inputs = (
    <div className="space-y-5" data-testid="cb-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Activity Details</h2>
      <p className="text-slate-400 text-sm mb-5">Based on standard MET values.</p>
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
      <SelectField id="cb-activity" label="Activity" value={activity} onChange={setActivity} options={ACTIVITIES_MET.map((a) => ({ value: a.value, label: `${a.label} (MET ${a.met})` }))} testId="cb-activity-select" required />
      <NumberField id="cb-min" label="Duration" value={minutes} onChange={setMinutes} placeholder="e.g. 45" suffix="minutes" min={1} max={600} testId="cb-minutes-input" required />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Calories Burned" value={result.kcal.toLocaleString()} unit="kcal" accent="from-amber-500 to-orange-500" testId="cb-result-value" />
      <div className="mt-8 space-y-1">
        <StatRow label="Per minute" value={result.perMin} unit="kcal/min" testId="cb-per-min" />
        <StatRow label="MET value" value={met} testId="cb-met" />
        <StatRow label="Activity" value={ACTIVITIES_MET.find((a) => a.value === activity)?.label} testId="cb-activity-label" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Calorie burn varies with intensity, fitness level and individual physiology. Treat this as a guideline, not a precise count.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter your weight, activity and duration to see calories burned." />
  );

  return (
    <CalculatorLayout
      slug="calorie-burn" title="Calorie Burn Calculator"
      subtitle="Estimate calories burned during running, cycling, swimming, strength training and 12 other activities."
      metaDescription="Calorie Burn Calculator to estimate calories burned for 15+ activities using standard MET values."
      icon={Zap}
      accentGradient="from-amber-500 to-orange-500" accentColor="text-amber-400"
      badgeBg="bg-amber-500/10" badgeBorder="border-amber-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Formula:</strong> kcal/min = MET × 3.5 × weight (kg) ÷ 200</p>
        <p>Total = kcal/min × minutes</p>
      </>}
    />
  );
};

export default CalorieBurnCalculatorPage;
